import i18n from "@/i18n";
import { type ApiErrorCode, getProblemDetails } from "./problemDetails";

// One resolver per catalogue code (typed exhaustively against ApiErrorCode, so a code
// added to ERROR_CODES without a translation fails the build). Keys mirror the backend
// message templates; parameterized ones interpolate the problem's args extension.
const API_ERROR_MESSAGES: Record<ApiErrorCode, (args: Record<string, unknown>) => string> = {
  "test-read-denied": () => i18n.t("common:apiErrors.test-read-denied"),
  "test-modify-denied": () => i18n.t("common:apiErrors.test-modify-denied"),
  "test-create-denied": () => i18n.t("common:apiErrors.test-create-denied"),
  "report-denied": () => i18n.t("common:apiErrors.report-denied"),
  "lab-users-manage-denied": () => i18n.t("common:apiErrors.lab-users-manage-denied"),
  "lab-settings-modify-denied": () => i18n.t("common:apiErrors.lab-settings-modify-denied"),
  "not-authorized": () => i18n.t("common:apiErrors.not-authorized"),
  "test-already-exists": () => i18n.t("common:apiErrors.test-already-exists"),
  "sample-id-belongs-to-another-sample": (args) => i18n.t("common:apiErrors.sample-id-belongs-to-another-sample", args),
  "sample-reference-in-use": () => i18n.t("common:apiErrors.sample-reference-in-use"),
  "laboratory-not-found": () => i18n.t("common:apiErrors.laboratory-not-found"),
  "processing-test-not-found": () => i18n.t("common:apiErrors.processing-test-not-found"),
  "comment-required": () => i18n.t("common:apiErrors.comment-required"),
  "specimen-reference-required": () => i18n.t("common:apiErrors.specimen-reference-required"),
  "specimen-reference-in-use": () => i18n.t("common:apiErrors.specimen-reference-in-use"),
  "ids-required": () => i18n.t("common:apiErrors.ids-required"),
  "attachment-name-empty": () => i18n.t("common:apiErrors.attachment-name-empty"),
  "attachment-name-reserved": () => i18n.t("common:apiErrors.attachment-name-reserved"),
  "attachment-name-invalid": () => i18n.t("common:apiErrors.attachment-name-invalid"),
  "relationship-self-reference": () => i18n.t("common:apiErrors.relationship-self-reference"),
  "relationship-missing-targets": () => i18n.t("common:apiErrors.relationship-missing-targets"),
  "test-invalid-status": (args) => i18n.t("common:apiErrors.test-invalid-status", args),
  "test-conditions-denied": () => i18n.t("common:apiErrors.test-conditions-denied"),
  "test-conditions-locked": () => i18n.t("common:apiErrors.test-conditions-locked"),
  "test-conditions-changed": () => i18n.t("common:apiErrors.test-conditions-changed"),
  "test-conditions-invalid": (args) => i18n.t("common:apiErrors.test-conditions-invalid", args),
  "test-conditions-not-ready": () => i18n.t("common:apiErrors.test-conditions-not-ready"),
  "send-back-invalid-status": (args) => i18n.t("common:apiErrors.send-back-invalid-status", args),
  "too-many-report-tests": (args) => i18n.t("common:apiErrors.too-many-report-tests", args),
  "too-many-ids": (args) => i18n.t("common:apiErrors.too-many-ids", args),
  "duplicate-emails": (args) => i18n.t("common:apiErrors.duplicate-emails", args),
  "report-tests-not-found": (args) => i18n.t("common:apiErrors.report-tests-not-found", args),
  "fdf-not-supported": () => i18n.t("common:apiErrors.fdf-not-supported"),
  "fdf-template-missing": (args) => i18n.t("common:apiErrors.fdf-template-missing", args),
  "processing-data-missing": (args) => i18n.t("common:apiErrors.processing-data-missing", args),
  "combined-method-processing-only": (args) => i18n.t("common:apiErrors.combined-method-processing-only", args),
  "processing-test-in-progress": () => i18n.t("common:apiErrors.processing-test-in-progress"),
  "malformed-completion-metadata": () => i18n.t("common:apiErrors.malformed-completion-metadata"),
  "acquisition-tests-incomplete": () => i18n.t("common:apiErrors.acquisition-tests-incomplete"),
  "pd-approval-required": () => i18n.t("common:apiErrors.pd-approval-required"),
  "retest-no-matching-acquisition-tests": () => i18n.t("common:apiErrors.retest-no-matching-acquisition-tests"),
  "report-container-not-found": () => i18n.t("common:apiErrors.report-container-not-found"),
  "report-template-required": () => i18n.t("common:apiErrors.report-template-required"),
  "report-no-eligible-tests": () => i18n.t("common:apiErrors.report-no-eligible-tests"),
};

function isKnownCode(code: string | undefined): code is ApiErrorCode {
  return code !== undefined && Object.hasOwn(API_ERROR_MESSAGES, code);
}

// Translated message for a known errorCode; otherwise the server's detail/title; undefined
// when the error carries no usable problem body.
export function translateApiError(error: unknown): string | undefined {
  const problem = getProblemDetails(error);
  if (!problem) return undefined;
  if (isKnownCode(problem.errorCode)) {
    return API_ERROR_MESSAGES[problem.errorCode](problem.args ?? {});
  }
  return problem.detail?.trim() || problem.title?.trim() || undefined;
}
